import React, { useEffect, useState } from 'react';
import { 
  fetchPortalNotificationRuns, 
  fetchPendingPortalNotifications,
  createPortalNotificationRun,
  fetchPortalNotificationRunRecords,
} from '../../actions/global';
import AgGrid from '@components/AgGrid/AgGrid';
import CircularProgress from '@material-ui/core/CircularProgress';
import AddIcon from '@material-ui/icons/Add';
import RefreshIcon from '@material-ui/icons/Refresh';
import IconButton from '@material-ui/core/IconButton';
import Fab from '@material-ui/core/Fab';
import Tooltip from '@material-ui/core/Tooltip';
import TablePagination from '@material-ui/core/TablePagination';
import { useDispatch } from 'react-redux';
import { showSuccess, showError } from '@actions/global';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { confirm, LinkCellRenderer, DropdownInput } from '@trueprodigy/components';
import moment from 'moment';
import { getNumberFormat } from '@utils';
import Typography from '@material-ui/core/Typography';
import PendingRequestsDisplay from './PendingRequestsDisplay';
import NavigateNextIcon from '@material-ui/icons/NavigateNext';
import Breadcrumbs from '@material-ui/core/Breadcrumbs';

const getElapsedTime = (startDt, finishDt) => {
  if (startDt && finishDt) {
    const format = 'HH:mm:ss';

    return moment.utc(moment(finishDt).diff(moment(startDt))).format(format);
  }
  return '';
};

const RunsList = ({ setConfigureEmail }) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);

  const [jobs, setJobs] = useState([]);
  const [jobRecords, setJobRecords] = useState([]);

  const [totalRecords, setTotalRecords] = useState(0);

  const [selectedRun, setSelectedRun] = useState(null);
  const [selectedPending, setSelectedPending] = useState(false);
  const [numPendingRuns, setNumPendingRuns] = useState(0);
  const [filter, setFilter] = useState('All');

  const [rowsPerPage, setRowsPerPage] = useState(200);

  const fetchData = async () => {
    try {
      setLoading(true);
      const reqData = await Promise.all([
        dispatch(fetchPendingPortalNotifications()),
        dispatch(
          fetchPortalNotificationRuns({
            page: page + 1,
            pageSize: rowsPerPage,
          })
        ),
      ]);

      if (reqData?.[0]?.results?.[0]) {
        setNumPendingRuns(
          reqData[0].results[0].pendingPortalNotifications || 0
        );
      }

      setTotalRecords(reqData?.[1]?.recordCount || 0);
      if (reqData?.[1]?.results) {
        setJobs(reqData?.[1]?.results);
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      setLoading(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  useEffect(() => {
    fetchData();
  }, [page, rowsPerPage]);

  const fetchJobRecords = async () => {
    try {
      setLoading(true);
      const res = await dispatch(
        fetchPortalNotificationRunRecords({
          runID: selectedRun,
          page: 1,
          pageSize: rowsPerPage,
          filter: filter === 'All' ? null : filter,
        }),
      );
      if (res && res?.results) {
        setJobRecords(res.results);
      }
      setLoading(false);
    } catch (err) {
      console.log(err);
      setLoading(false);
    }
  };

  useEffect(() => {
    selectedRun && fetchJobRecords();
  }, [selectedRun, filter]);

  const buildColumnDefsRecords = () => [
    {
      headerName: 'Detail ID',
      field: 'detailID',
    },
    {
      headerName: 'Property ID',
      field: 'pID',
      cellRenderer: LinkCellRenderer,
      cellRendererParams: {
        onClick: (_, data) =>
          window.open(
            `/appraisal/property/${data.pID}/${data.pYear}/${data.pVersion}/${data.pRollCorr}`,
            '_blank',
          ),
      },
    },
    {
      headerName: 'Owner Name',
      field: 'ownerName',
    },
    {
      headerName: 'Start Date',
      field: 'startDt',
      valueFormatter: ({ value }) =>
        value ? moment(value).format('MM/DD/YYYY HH:mm:ss') : '',
    },
    {
      headerName: 'End Date',
      field: 'endDt',
      valueFormatter: ({ value }) =>
        value ? moment(value).format('MM/DD/YYYY HH:mm:ss') : '',
    },
    {
      headerName: 'Processed',
      field: 'processed',
      valueFormatter: ({ value }) => (value ? 'Yes' : 'No'),
    },
    {
      headerName: 'Has Error',
      field: 'hasError',
      valueFormatter: ({ value }) => (value ? 'Yes' : 'No'),
    },
    {
      headerName: 'Error Message',
      field: 'errorMessage',
    },
  ];

  const buildColumnDefs = () => [
    {
      headerName: 'Run ID',
      field: 'runID',
    },
    {
      headerName: 'Process',
      field: 'processDisplay',
      flex: 1,
    },
    {
      headerName: 'Status',
      field: 'runStatus',
    },
    {
      headerName: 'Error',
      field: 'errorMessage',
    },
    {
      headerName: 'Start Dt',
      field: 'startDt',
    },
    {
      headerName: 'End Dt',
      field: 'finishDt',
    },
    {
      headerName: 'Elapsed Time',
      field: 'elapsedTime',
      minWidth: 120,
      cellRenderer: ({ data }) =>
        data ? getElapsedTime(data.startDt, data.finishDt) : '',
    },
    {
      headerName: '# of Records',
      field: 'totalRecords',
      cellRenderer: LinkCellRenderer,
      cellRendererParams: {
        onClick: async (_, data) => {
          setSelectedRun(data.runID);
          setFilter('');
        },
        valueFormatter: (value) => getNumberFormat(value),
      },
    },
    {
      headerName: '# of Success',
      field: 'successRecords',
      cellRenderer: LinkCellRenderer,
      cellRendererParams: {
        onClick: async (_, data) => {
          setSelectedRun(data.runID);
          setFilter('Success');
        },
        valueFormatter: (value) => getNumberFormat(value),
      },
    },
    {
      headerName: '# of Errors',
      field: 'errorRecords',

      cellRenderer: LinkCellRenderer,
      cellRendererParams: {
        onClick: async (_, data) => {
          setSelectedRun(data.runID);
          setFilter('Error');
        },
        valueFormatter: (value) => getNumberFormat(value),
      },
    },
    {
      headerName: 'Create Dt',
      field: 'createDt',
    },
    {
      headerName: 'Created By',
      field: 'createdBy',
    },
  ];

  const handleInit = () => {
    setSelectedRun(null);
    setSelectedPending(false);
    setFilter('All');
  };

  if(selectedPending) {
    return <PendingRequestsDisplay setSelectedPending={setSelectedPending} />;
  }

  return (
    <div>
      {selectedPending ? (
        <TitleContainer>
          <Breadcrumbs
            separator={<NavigateNextIcon fontSize="small" />}
            aria-label="breadcrumb"
          >
            <Link 
              color="inherit" 
              onClick={handleInit}
              style={{ cursor: 'pointer' }}
            >
              Portal Notification Runs
            </Link>
            <Typography color="textPrimary">Pending Notifications</Typography>
          </Breadcrumbs>
        </TitleContainer>
      ) : !selectedRun ? (
        <TitleContainer>
          <Title>Electronic Delivery Portal Notification Runs</Title>
          <ControlsWrapper>
            <PendingRunsWrapper>
              Pending Portal Delivery Notifications:{' '}
              <span onClick={() => setSelectedPending(true)}>{numPendingRuns}</span>
            </PendingRunsWrapper>
            <span
              onClick={() => setConfigureEmail(true)}
              style={{ marginRight: 20, cursor: 'pointer', color: '#0000ee', textDecoration: 'underline' }}
            >
              Configure Electronic Delivery Notification Email
            </span>
            <IconButton style={{ marginRight: 10 }} onClick={fetchData}>
              <RefreshIcon />
            </IconButton>
            <Tooltip title="Create New Notifications Reminder Run">
              <AddButton
                onClick={() => {
                  confirm('', {
                    description: (
                      <>
                        <div>
                          This job will send portal delivery notifications for all pending deliveries.
                          <br />
                          <br />
                        </div>
                        Do you wish to continue
                      </>
                    ),
                    noAction: 'No',
                    title: 'Electronic Delivery Requests Portal Delivery Notifications',
                    yesAction: 'Yes',
                  }).then(async () => {
                    try {
                      await dispatch(createPortalNotificationRun());
                      dispatch(showSuccess('New run created.'));
                      fetchData();
                    } catch (error) {
                      dispatch(showError('Failed to create run'));
                    }
                  });
                }}
              />
            </Tooltip>
          </ControlsWrapper>
        </TitleContainer>
      ) : (
        <TitleContainer>
          <Breadcrumbs
            separator={<NavigateNextIcon fontSize="small" />}
            aria-label="breadcrumb"
          >
            <Link color="inherit" onClick={handleInit}>
              Portal Notifications Reminder Runs
            </Link>
            <Typography color="textPrimary">{selectedRun}</Typography>
            <Typography color="textPrimary">{filter || 'Records'}</Typography>
          </Breadcrumbs>
          <div style={{ width: 250 }}>
            <DropdownInput
              addEmptyOption={false}
              options={[
                { label: 'All', value: 'All' },
                { label: 'Success', value: 'Success' },
                { label: 'Error', value: 'Error' },
              ]}
              placeholder=""
              input={{
                value: filter,
                onChange: ({ value }) => setFilter(value),
              }}
              meta={{ touched: true }}
            />
          </div>
        </TitleContainer>
      )}

      <TableContainer className="ag-theme-material">
        {loading ? (
          <div style={{ textAlign: 'center' }}>
            <CircularProgress />
          </div>
        ) : (
          <AgGrid
            name="MassUpdatePropertyBeta"
            allowHorizontalScroll={true}
            defaultColDef={{ 
              resizable: true, 
              sortable: true,
              minWidth: 100,
            }}
            columnDefs={selectedRun ? buildColumnDefsRecords() : buildColumnDefs()}
            rowData={selectedRun ? jobRecords : jobs}
            onGridReady={(params) => {
              params.columnApi.autoSizeAllColumns();
            }}
          />
        )}
      </TableContainer>
      {!selectedRun && totalRecords ? (
        <TablePagination
          component="div"
          count={totalRecords}
          page={page}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[50, 100, 200, 500, 1000, 5000, 10000]}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      ) : null}
    </div>
  );
};

RunsList.propTypes = {
  setConfigureEmail: PropTypes.func.isRequired,
};

const TableContainer = styled.div`
  padding: 20px;
  height: calc(100vh - 240px);
  background: #fff;
  overflow: auto;
  width: 100%;
`;

const TitleContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 20px 0;
  background: #fff;
`;

const Title = styled.div`
  font-family: Roboto, sans-serif;
  margin: 10px 0;
  padding: 0 0 0 10px;
  font-size: 24px;
  font-weight: 500;
  text-transform: uppercase;
`;

const ControlsWrapper = styled.div`
  display: flex;
  align-items: center;
  flex-grow: 1;
  justify-content: flex-end;
`;

const MuiFab = styled(Fab)`
  && {
    z-index: 9;
    margin-left: 10px;
    width: ${({ size }) => (size === 'small' ? '35px' : '56px')};
    height: ${({ size }) => (size === 'small' ? '20px' : '56px')};
    background-color: #36449d;
    cursor: ${({ preview }) => !preview && 'pointer'};
  }
`;

const AddButton = React.forwardRef((props, ref) => (
  <MuiFab aria-label="Add" color="primary" {...props} ref={ref}>
    <AddIcon />
  </MuiFab>
));

AddButton.displayName = 'AddButton';

const PendingRunsWrapper = styled.div`
  margin-right: 30px;

  span {
    color: #0000ee;
    font-weight: 600;
    text-decoration: underline;
    cursor: pointer;
  }
`;

export default RunsList;
